import React, {useRef} from 'react';
import {SingleGameRegularTemplateComponent} from './regular-template/single-game-regular-template.component';
import {GameInfo, OpenGameParameters} from '../../services/games/game-info';
import {CssClassNameBuilder} from '../../../utils/css-class-name-builder';
import {observer} from 'mobx-react';
import {GAME_BOX, SWIPER_MODE} from './css-constants';
import {SingleGameJackpotTemplateComponent} from './jackpot-template/single-game-jackpot-template.component';
import {JACKPOTS_ITEM, LIVE_CASINO_ITEM, LIVE_STREAMING_ITEM, STATIC_VIDEO_ITEM} from '../../themes/css-global-classes';
import {CyClass} from '../../../common/cy-classes';
import {SingleGameLiveTemplateComponent} from './live-template/single-game-live-template.component';
import {SingleGameVideoTemplate, VIDEO_ITEM_TYPE} from './video-template/single-game-video-template.component';
import {GameIconAnimationState} from './common/parts/game-image/game-image.component';
import {ComponentServicesPick} from "../../services/component-services-pick";
import {useServices} from "../../react-hooks/use-services.hook";
import {withServices} from "../../react-hooks/with-services";
import {SingleGameSkeletonComponent} from "./single-game-skeleton.component";

interface SingleGameComponentProps extends ComponentServicesPick<'lobbyGameInfoPreview'>{
    gameType: number;
    seoCategory: string;
    listTitle: string;
    listPosition?: number,
    AdminListID?: number,
    AIListTypeID?: number;
    templateType?: any;
    gameIndex?: number;
    skipIntermediatePage?: boolean;
    disableGameInfoHover: boolean;
    isInsideSwiper?: boolean;
    aiEngineName?: string;
    useSquareIcons?: boolean;
    showNumberBadge?: boolean;
    intersectionTarget?: HTMLDivElement;
    hasTwoLinesTitle?: boolean;
    isLastGametype?: boolean;
    onBringInCentralView?: (gameIndex: number) => void;
}

const ComponentBody: React.FC<SingleGameComponentProps> = observer((props) => {
    const services = useServices();
    const animatedIconState = useRef<GameIconAnimationState>('static');
    const gameInfo = services.games.gamesMap[props.gameType];

    const _openGame = async (openInRealMode: boolean) => {


        const openGameParams: OpenGameParameters = {
            isRealMode: openInRealMode,
            pageName: props.seoCategory,
            listTitle: props.listTitle,
            listPosition: props.listPosition,
            AdminListID: props.AdminListID,
            AIListTypeID: props.AIListTypeID,
            gameIndex: props.gameIndex,
            aiEngine: props.aiEngineName,
            showIntermediatePage: !props.skipIntermediatePage,
            animatedIconState: animatedIconState.current
        };

        await gameInfo.open(openGameParams)
    }

    const sendAnalytics = (label: string) => {
        services.analytics.sendElasticInfoEvent({eventType:"CGP_Game_Click",category:props.seoCategory, action: props.listTitle, label: label}, true);
    }


    const openRealGame = async () => {
        await _openGame(true);
    };

    const openAnonymousGame = async () => {
        await _openGame(false);
    };

    const openInfoPage = async () => {
        const infoPageUrl = await gameInfo.getInfoPageURL();
        services.location.push(infoPageUrl);
        sendAnalytics(`SEO_${gameInfo.gameType}`);
    };

    const _allowPreview = () => {
        return props.lobbyGameInfoPreview.allowGamePreview
            && !props.disableGameInfoHover;
    }

    const setAnimatedIconState = (state: GameIconAnimationState) => {
        animatedIconState.current = state;
    }

    const onMouseEnter = () => {
        if (_allowPreview()) {
            props.lobbyGameInfoPreview.setGamePreview(props.gameType);
        }
    };

    const onMouseLeave = () => {
        if (_allowPreview()) {
            props.lobbyGameInfoPreview.clearGamePreview();
        }
    };

    const _buildComponentClasses = (gameInfo: GameInfo) => {
        let builder = new CssClassNameBuilder();

        builder.append(GAME_BOX);

        if(props.isInsideSwiper) {
            builder = builder.append(SWIPER_MODE);
        }

        if (gameInfo) {
            builder = builder.append(`game-group-${toCssClass(gameInfo.gameGroup)}`)
                .append(gameInfo.gameCategoryClassName)
                .append(`game-company-${toCssClass(gameInfo.companyName)}`)
                .append(`game-type-${gameInfo.gameType}`);
        }

        if(props.skipIntermediatePage) {
            builder.append(CyClass('cy-skip-intermediate-page'));
        }

        return builder.build();

    }


    if(!gameInfo) {
        return null;
    }

    try {

        const classes = _buildComponentClasses(gameInfo);

        switch (props.templateType) {

            case JACKPOTS_ITEM:
                return (
                    <SingleGameJackpotTemplateComponent key={props.templateType}
                                                        gameInfo={gameInfo}
                                                        className={classes}
                                                        onOpenRealGame={openRealGame}
                    />
                );

            case LIVE_CASINO_ITEM:
                return (
                    <SingleGameLiveTemplateComponent key={props.templateType}
                                                     tableConfig={gameInfo}
                                                     listTitle={props.listTitle}
                                                     gameIndex={props.gameIndex}
                                                     seoCategory={props.seoCategory}
                    />
                );

            case LIVE_STREAMING_ITEM:
                return (
                    <SingleGameVideoTemplate key={props.templateType}
                                             gameInfo={gameInfo}
                                             onOpenRealGame={openRealGame}
                                             intersectionTarget={props.intersectionTarget}
                                             videoItemType={VIDEO_ITEM_TYPE.LIVE_STREAM}
                                             gameIndex={props.gameIndex}
                                             onBringInCentralView={props.onBringInCentralView}
                    />
                );

            case STATIC_VIDEO_ITEM:
                return (
                    <SingleGameVideoTemplate key={props.templateType}
                                             gameInfo={gameInfo}
                                             onOpenRealGame={openRealGame}
                                             intersectionTarget={props.intersectionTarget}
                                             videoItemType={VIDEO_ITEM_TYPE.STATIC}
                                             isLastGametype={props.isLastGametype}
                                             gameIndex={props.gameIndex}
                                             onBringInCentralView={props.onBringInCentralView}
                    />
                );

            default:
                return (
                    <SingleGameRegularTemplateComponent key="SINGLE_GAME_REGULAR_TEMPLATE"
                                                        className={classes}
                                                        gameInfo={gameInfo}
                                                        disableGameInfoHover={props.disableGameInfoHover}
                                                        onMouseEnter={onMouseEnter}
                                                        onMouseLeave={onMouseLeave}
                                                        onOpenRealGame={openRealGame}
                                                        onOpenAnonymousGame={openAnonymousGame}
                                                        gameIndex={props.gameIndex}
                                                        showNumberBadge={props.showNumberBadge}
                                                        useSquareIcons={props.useSquareIcons}
                                                        onOpenInfoPage={openInfoPage}
                                                        onAnimationIconStateChange={setAnimatedIconState}
                                                        hasTwoLinesTitle={props.hasTwoLinesTitle || false}
                    />
                );
        }

    } catch (err) {
        services.logger.error(`Failed to render SingleGameComponent for game ${gameInfo.gameType} - ${gameInfo.title} in list ${props.listTitle}`, err);
        return null;
    }

});

export const SingleGameComponent = withServices(ComponentBody, ['lobbyGameInfoPreview'], {
    renderSkeleton: props => {
        return (
            <SingleGameSkeletonComponent
                templateType={props.templateType}
                useSquareIcons={props.useSquareIcons}
            />
        );
    }
});

function toCssClass(str: string){
    try{
        return str.replace(/\s+/g, '-').toLowerCase();
    }
    catch(e){
        return '';
    }
}

